import { useAppSelector } from 'store/hooks';
import React from 'react';
import { IOrder } from '../model/Order';
import { add, isAfter } from 'date-fns';
import { orderApi } from '../orderApi';
import { useSnackbar } from 'notistack';
import { v4 } from 'uuid';
import {
    MESSAGE_CUSTOMER_ORDER_CLOSING_ERROR,
    MESSAGE_CUSTOMER_ORDER_CLOSING_PENDING,
    MESSAGE_CUSTOMER_ORDER_CLOSING_SUCCESS
} from 'config/messages';
import { OperationStatus } from 'components/operation/models/OperationModel';
import { Permission, Resource, permission } from '@pepperhq/auth-client';

export function useForceClose(order: IOrder | undefined, onClosed: () => void) {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const { claims } = useAppSelector(state => state.auth);
    const hasPermissionsToClose = React.useMemo(
        () => claims.hasPermissions(permission(Resource.Order, Permission.write)),
        [claims]
    );
    const isClosable = React.useMemo(() => {
        if (order?.isOpen && hasPermissionsToClose) {
            const currentDate = new Date();
            const createdDate = new Date(order?.createdAt);
            return isAfter(currentDate, add(createdDate, { days: 1 }));
        }
        return false;
    }, [order?.createdAt, order?.isOpen, hasPermissionsToClose]);
    const handleCloseError = React.useCallback(() => {
        setOpen(false);
        setLoading(false);
        enqueueSnackbar(MESSAGE_CUSTOMER_ORDER_CLOSING_ERROR, { variant: 'error' });
    }, [enqueueSnackbar]);
    const handleCloseSuccess = React.useCallback(() => {
        setOpen(false);
        setLoading(false);
        onClosed();
        enqueueSnackbar(MESSAGE_CUSTOMER_ORDER_CLOSING_SUCCESS, { variant: 'success' });
    }, [onClosed, enqueueSnackbar]);
    const handleCloseOrder = React.useCallback(async () => {
        setLoading(true);
        const result = await orderApi.update(order?.id, {
            body: undefined,
            queryParameters: [{ key: 'forceClose', value: true }],
            headers: {
                'x-pepper-req-nonce': v4()
            }
        });
        if (!result.ok) {
            return handleCloseError();
        }
        enqueueSnackbar(MESSAGE_CUSTOMER_ORDER_CLOSING_PENDING, { variant: 'info' });
        const operationResult = await orderApi.pollOC2Operation(result.body.id, result.body.etaMs);
        if (operationResult.status === OperationStatus.ERROR) {
            return handleCloseError();
        }
        handleCloseSuccess();
    }, [handleCloseError, handleCloseSuccess, order?.id, enqueueSnackbar]);
    const onForceClose = React.useCallback(() => {
        setOpen(true);
    }, []);
    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, []);
    const modalProps = React.useMemo(
        () => ({
            open,
            onAgree: handleCloseOrder,
            onClose: handleClose,
            loading
        }),
        [handleClose, handleCloseOrder, loading, open]
    );
    return {
        isClosable,
        isLoading: loading,
        onForceClose,
        modalProps
    };
}
